import { useState, useEffect, useCallback } from 'react';
import { 
  Calendar, Plus, Users, DollarSign, TrendingUp, AlertCircle,
  CheckCircle, Clock, MapPin, Home, Star, MessageSquare,
  ChevronLeft, ChevronRight, Filter, Download, Loader2
} from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface Reservation {
  id: string;
  propertyName: string;
  guestName: string;
  guestEmail: string;
  guestPhone: string;
  checkIn: string;
  checkOut: string;
  nights: number;
  guests: number;
  totalAmount: number;
  platform: 'airbnb' | 'vrbo' | 'booking' | 'direct';
  status: 'confirmed' | 'pending' | 'cancelled' | 'checked-in' | 'checked-out';
  specialRequests?: string;
}

interface Property {
  id: string;
  name: string;
  address: string;
  type: 'apartment' | 'house' | 'condo';
  bedrooms: number;
  bathrooms: number;
  maxGuests: number;
  basePrice: number;
  cleaningFee: number;
}

export default function ReservationManager() {
  const { user, contexts, activeContextId } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar');
  const [selectedProperty, setSelectedProperty] = useState<string>('all');
  const [showNewReservation, setShowNewReservation] = useState(false);
  const [properties, setProperties] = useState<Property[]>([]);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadProperties = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const active = contexts.find(c => c.id === activeContextId);
      const condoId = active?.condoId || contexts.find(c => c.kind === 'condo')?.condoId || user?.condoId;
      if (!condoId) { setProperties([]); return; }
      // Properties are condo/unit memberships, not HOME rental properties.
      const res = await api.get('/condos/my-properties');
      const data = res.data?.data ?? res.data;
      const propsList = Array.isArray(data) ? data : data?.properties || [];
      setProperties(propsList.map((p: any) => ({ id: p.unitId || p.condoId, name: p.unitNumber ? `${p.condoName} · ${p.unitNumber}` : p.condoName || 'Condominio', address: [p.city, p.country].filter(Boolean).join(', '), type: 'condo', bedrooms: 0, bathrooms: 0, maxGuests: 0, basePrice: 0, cleaningFee: 0 })));
    } catch (err: any) {
      // If endpoint doesn't exist or fails, show empty state
      console.error('Error loading properties:', err);
      setProperties([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProperties();
  }, [loadProperties, contexts, activeContextId, user?.condoId]);

  const [newReservation, setNewReservation] = useState({
    propertyId: '',
    guestName: '',
    guestEmail: '',
    guestPhone: '',
    checkIn: '',
    checkOut: '',
    guests: 1,
    platform: 'direct' as Reservation['platform'],
    specialRequests: '',
  });

  const handleNewReservation = async () => {
    try {
      await api.post('/home/reservations', newReservation);
      setShowNewReservation(false);
      setNewReservation({
        propertyId: '', guestName: '', guestEmail: '', guestPhone: '',
        checkIn: '', checkOut: '', guests: 1, platform: 'direct', specialRequests: '',
      });
      loadProperties();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Error al crear reservación');
    }
  };

  const getStatusBadge = (status: string) => {
    const config: Record<string, { color: string; label: string }> = {
      confirmed: { color: 'bg-blue-500/10 text-blue-400 border-blue-500/20', label: 'Confirmada' },
      pending: { color: 'bg-amber-500/10 text-amber-400 border-amber-500/20', label: 'Pendiente' },
      cancelled: { color: 'bg-red-500/10 text-red-400 border-red-500/20', label: 'Cancelada' },
      'checked-in': { color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', label: 'Check-in' },
      'checked-out': { color: 'bg-slate-500/10 text-[var(--v-text-secondary)] border-slate-500/20', label: 'Check-out' },
    };
    const s = config[status] || { color: 'bg-slate-500/10 text-[var(--v-text-secondary)]', label: status };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${s.color}`}>
        {s.label}
      </span>
    );
  };

  const getPlatformLabel = (platform: string) => {
    const labels: Record<string, string> = { airbnb: 'Airbnb', vrbo: 'VRBO', booking: 'Booking.com', direct: 'Directo' };
    return labels[platform] || platform;
  };

  const filteredReservations = selectedProperty === 'all' 
    ? reservations 
    : reservations.filter(r => r.propertyName === properties.find(p => p.id === selectedProperty)?.name);

  // Calendar view helpers
  const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();
  const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

  const renderCalendarView = () => (
    <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-border-light)] p-4">
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1))} className="p-2 hover:bg-[var(--v-surface-2)] rounded-lg">
          <ChevronLeft className="w-5 h-5 text-[var(--v-text-secondary)]" />
        </button>
        <h3 className="text-lg font-semibold text-[var(--v-text-primary)]">
          {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
        </h3>
        <button onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1))} className="p-2 hover:bg-[var(--v-surface-2)] rounded-lg">
          <ChevronRight className="w-5 h-5 text-[var(--v-text-secondary)]" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-2">
        {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map(day => (
          <div key={day} className="text-center text-xs text-[var(--v-text-tertiary)] font-medium py-2">{day}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {Array.from({ length: firstDayOfMonth }).map((_, i) => (
          <div key={`empty-${i}`} className="aspect-square" />
        ))}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const dayReservations = filteredReservations.filter(r => r.checkIn === dateStr);
          return (
            <div key={day} className="aspect-square border border-[var(--v-border-light)] rounded-lg p-1 text-xs">
              <span className="text-[var(--v-text-secondary)]">{day}</span>
              {dayReservations.map(r => (
                <div key={r.id} className="mt-1 px-1 py-0.5 rounded bg-blue-500/10 text-blue-400 truncate text-[10px]">
                  {r.guestName}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderListView = () => (
    <div className="space-y-3">
      {filteredReservations.length === 0 ? (
        <div className="bg-[var(--v-surface-1)] p-8 rounded-xl border border-[var(--v-border-light)] text-center">
          <Calendar className="w-12 h-12 text-[var(--v-text-tertiary)] mx-auto mb-3 opacity-50" />
          <p className="text-[var(--v-text-secondary)]">No hay reservaciones</p>
        </div>
      ) : (
        filteredReservations.map(res => (
          <div key={res.id} className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-border-light)]">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h4 className="font-semibold text-[var(--v-text-primary)]">{res.propertyName}</h4>
                <p className="text-sm text-[var(--v-text-secondary)]">{res.guestName} • {res.guests} huéspedes</p>
              </div>
              {getStatusBadge(res.status)}
            </div>
            <div className="flex flex-wrap items-center gap-4 text-xs text-[var(--v-text-tertiary)]">
              <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{res.checkIn} → {res.checkOut}</span>
              <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" />${res.totalAmount}</span>
              <span>{getPlatformLabel(res.platform)}</span>
              {res.nights && <span>{res.nights} noches</span>}
            </div>
          </div>
        ))
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-[var(--v-text-primary)]">Reservas y Propiedades</h2>
          <p className="text-[var(--v-text-secondary)] mt-1">Gestiona tus propiedades y reservaciones</p>
        </div>
        {properties.length > 0 && (
          <button 
            onClick={() => setShowNewReservation(true)}
            className="bg-[var(--v-teal-400)] hover:bg-[var(--v-teal-500)] text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Nueva Reserva
          </button>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="bg-[var(--v-danger-bg)] border border-[var(--v-danger)]/30 rounded-lg p-3 flex items-center gap-2 text-[var(--v-danger)]">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12 text-[var(--v-text-secondary)]">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Cargando propiedades...
        </div>
      )}

      {/* Empty State — No Properties */}
      {!loading && properties.length === 0 && (
        <div className="bg-[var(--v-surface-1)] p-12 rounded-xl border border-[var(--v-border-light)] text-center">
          <Home className="w-16 h-16 text-[var(--v-text-tertiary)] mx-auto mb-4 opacity-50" />
          <h3 className="text-lg font-semibold text-[var(--v-text-primary)] mb-2">Aún no tienes propiedades vinculadas</h3>
          <p className="text-[var(--v-text-secondary)] mb-4">
            Únete a un condominio para empezar a gestionar tus propiedades y reservas.
          </p>
          <button
            onClick={() => window.location.href = '/profile'}
            className="px-4 py-2 bg-[var(--v-teal-400)] text-white font-bold rounded-xl hover:bg-[var(--v-teal-500)] transition-colors"
          >
            Unirme a un condominio
          </button>
        </div>
      )}

      {/* Properties + Reservations */}
      {!loading && properties.length > 0 && (
        <>
          {/* View Toggle + Property Filter */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex bg-[var(--v-surface-2)] p-1 rounded-lg border border-[var(--v-border-light)]">
              <button
                onClick={() => setViewMode('calendar')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${viewMode === 'calendar' ? 'bg-[var(--v-surface-1)] text-[var(--v-teal-400)] shadow-sm' : 'text-[var(--v-text-secondary)]'}`}
              >
                Calendario
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${viewMode === 'list' ? 'bg-[var(--v-surface-1)] text-[var(--v-teal-400)] shadow-sm' : 'text-[var(--v-text-secondary)]'}`}
              >
                Lista
              </button>
            </div>
            <select
              value={selectedProperty}
              onChange={(e) => setSelectedProperty(e.target.value)}
              className="bg-[var(--v-surface-2)] text-[var(--v-text-primary)] px-4 py-2 rounded-lg border border-[var(--v-border-light)] text-sm"
            >
              <option value="all">Todas las propiedades</option>
              {properties.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          {/* View */}
          {viewMode === 'calendar' ? renderCalendarView() : renderListView()}

          {/* Properties Summary */}
          <div className="bg-[var(--v-surface-1)] p-5 rounded-xl border border-[var(--v-border-light)]">
            <h4 className="font-semibold text-[var(--v-text-primary)] mb-4 flex items-center gap-2">
              <Home className="w-5 h-5 text-[var(--v-teal-400)]" />
              Mis Propiedades
            </h4>
            <div className="grid md:grid-cols-2 gap-4">
              {properties.map((prop) => {
                const propReservations = reservations.filter(r => r.propertyName === prop.name);
                const occupancy = propReservations.length > 0 ? Math.round((propReservations.length / 30) * 100) : 0;
                
                return (
                  <div key={prop.id} className="bg-[var(--v-surface-2)] p-4 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div>
                        <h5 className="text-[var(--v-text-primary)] font-medium">{prop.name}</h5>
                        <p className="text-[var(--v-text-secondary)] text-sm">{prop.bedrooms || 0} hab • {prop.bathrooms || 0} baños • Max {prop.maxGuests || 0}</p>
                        <div className="flex items-center gap-3 mt-2 text-sm">
                          <span className="text-emerald-600 dark:text-emerald-400 font-medium">${prop.basePrice || 0}/noche</span>
                          {prop.cleaningFee > 0 && <span className="text-[var(--v-text-tertiary)]">+ ${prop.cleaningFee} limpieza</span>}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-2xl font-bold ${occupancy > 70 ? 'text-emerald-600 dark:text-emerald-400' : occupancy > 40 ? 'text-amber-600 dark:text-amber-400' : 'text-[var(--v-text-tertiary)]'}`}>
                          {occupancy}%
                        </div>
                        <p className="text-[var(--v-text-tertiary)] text-xs">Ocupación</p>
                      </div>
                    </div>
                    <div className="mt-3 h-2 bg-[var(--v-surface-3)] rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full ${occupancy > 70 ? 'bg-emerald-500' : occupancy > 40 ? 'bg-amber-500' : 'bg-slate-500'}`}
                        style={{ width: `${occupancy}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
